/**
 * Registro do service worker e aviso de "nova versão" (equivalente web do
 * AtualizarAppModal do app nativo, que mandava para a loja).
 *
 * O vite-plugin-pwa está com `registerType: "prompt"`: o SW novo fica ESPERANDO e só
 * assume quando o usuário aceita (`aplicarAtualizacao()`), nunca no meio de uma batida.
 *
 * Uso:
 *   const { novaVersao } = useAtualizacaoSW();            // hook
 *   const sair = subscribe((estado) => { ... });           // fora do React
 *   await aplicarAtualizacao();                            // ativa o SW novo e recarrega
 *   DeviceEventEmitter.addListener("sw-nova-versao", fn);  // evento (mesma notícia)
 */
import { useSyncExternalStore } from "react";
import { registerSW } from "virtual:pwa-register";
import { DeviceEventEmitter } from "./events";

export type EstadoSW = {
  /** Há uma versão nova baixada esperando para assumir. */
  novaVersao: boolean;
  /** O app shell já está no cache — o app abre sem internet. */
  prontoOffline: boolean;
  /** O SW foi registrado com sucesso. */
  registrado: boolean;
};

/** Evento emitido no DeviceEventEmitter quando há versão nova esperando. */
export const EVENTO_NOVA_VERSAO = "sw-nova-versao";

/** Intervalo de verificação de versão nova com o app aberto. */
const INTERVALO_VERIFICACAO_MS = 60 * 60 * 1000;

let estado: EstadoSW = { novaVersao: false, prontoOffline: false, registrado: false };
let atualizarSW: ((recarregar?: boolean) => Promise<void>) | null = null;
let registroIniciado = false;
const ouvintes = new Set<(e: EstadoSW) => void>();

function definir(parcial: Partial<EstadoSW>) {
  estado = { ...estado, ...parcial };
  for (const fn of Array.from(ouvintes)) {
    try {
      fn(estado);
    } catch (e) {
      console.error("[sw-update] ouvinte falhou", e);
    }
  }
}

/** Registra o service worker (idempotente). Chamado no bootstrap (main.tsx). */
export function registrarServiceWorker(): void {
  if (registroIniciado) return;
  registroIniciado = true;
  if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) return;

  atualizarSW = registerSW({
    immediate: true,
    onNeedRefresh() {
      definir({ novaVersao: true });
      DeviceEventEmitter.emit(EVENTO_NOVA_VERSAO);
    },
    onOfflineReady() {
      definir({ prontoOffline: true });
    },
    onRegisteredSW(_url, registro) {
      definir({ registrado: true });
      if (!registro) return;
      // Procura versão nova de hora em hora e ao voltar para o app (só com rede: sem
      // internet o update() falharia à toa).
      const verificar = () => {
        if (!navigator.onLine || registro.installing) return;
        registro.update().catch(() => {});
      };
      setInterval(verificar, INTERVALO_VERIFICACAO_MS);
      document.addEventListener("visibilitychange", () => {
        if (document.visibilityState === "visible") verificar();
      });
    },
    onRegisterError(erro) {
      console.warn("[sw-update] falha ao registrar o service worker:", erro);
    },
  });
}

/** Assina mudanças de estado; devolve a função para cancelar. */
export function subscribe(fn: (e: EstadoSW) => void): () => void {
  ouvintes.add(fn);
  return () => {
    ouvintes.delete(fn);
  };
}

export function obterEstadoSW(): EstadoSW {
  return estado;
}

/** Ativa a versão nova (skipWaiting no SW) e recarrega a página. */
export async function aplicarAtualizacao(): Promise<void> {
  if (atualizarSW) {
    await atualizarSW(true);
    return;
  }
  window.location.reload();
}

/** Hook: estado atual do service worker (re-renderiza quando muda). */
export function useAtualizacaoSW(): EstadoSW & { aplicarAtualizacao: () => Promise<void> } {
  const atual = useSyncExternalStore(subscribe, obterEstadoSW, obterEstadoSW);
  return { ...atual, aplicarAtualizacao };
}
